import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, CreditCard, Loader2, Minus, Package, PartyPopper, Plus, ShieldCheck, Truck } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { fetchShopProduct, initShopPayment, verifyShopPayment, apiErr } from "@/lib/api";
import { loadRazorpay } from "@/lib/razorpay";
import { breadcrumbJsonLd } from "@/lib/seo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const inputCls = "mt-1.5 h-11 w-full rounded-md border border-white/10 bg-white/5 px-3 text-sm text-white placeholder:text-white/30 focus:border-gold-300/50 focus:outline-none";
const F = ({ label, children, full }) => (
    <div className={full ? "sm:col-span-2" : ""}>
        <label className="text-xs uppercase tracking-wider text-white/50">{label}</label>
        {children}
    </div>
);

export default function ShopProduct() {
    const { slug } = useParams();
    const [p, setP] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [qty, setQty] = useState(1);
    const [form, setForm] = useState({ full_name: "", mobile: "", email: "", address: "", pincode: "", city: "", state: "" });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null); // order_code

    useEffect(() => {
        setP(null); setDone(null); setQty(1);
        fetchShopProduct(slug).then(setP).catch(() => setNotFound(true));
        loadRazorpay();
    }, [slug]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const buyNow = async () => {
        for (const [k, label] of [["full_name", "name"], ["mobile", "mobile"], ["email", "email"], ["address", "address"], ["pincode", "pincode"], ["city", "city"], ["state", "state"]]) {
            if (!form[k].trim()) return toast.error(`Please enter your ${label}.`);
        }
        setBusy(true);
        try {
            const ok = await loadRazorpay();
            if (!ok) throw new Error("razorpay");
            const order = await initShopPayment(slug, { ...form, qty });
            const rzp = new window.Razorpay({
                key: order.razorpay_key_id,
                amount: order.amount * 100,
                currency: order.currency,
                name: "Abhijit JK",
                description: order.title,
                order_id: order.razorpay_order_id,
                prefill: { name: form.full_name, email: form.email, contact: form.mobile },
                theme: { color: "#D4AF37" },
                handler: async (resp) => {
                    try {
                        const v = await verifyShopPayment({
                            order_id: order.order_id,
                            razorpay_payment_id: resp.razorpay_payment_id,
                            razorpay_order_id: resp.razorpay_order_id,
                            razorpay_signature: resp.razorpay_signature,
                        });
                        setDone(v.order_code);
                        toast.success("Order placed successfully!");
                    } catch { toast.error("Payment verification failed. Contact support."); }
                    finally { setBusy(false); }
                },
                modal: { ondismiss: () => setBusy(false) },
            });
            rzp.on("payment.failed", () => { toast.error("Payment failed. Please try again."); setBusy(false); });
            rzp.open();
        } catch (e) {
            toast.error(apiErr(e, "Could not start payment. Please try again."));
            setBusy(false);
        }
    };

    if (notFound) return (
        <div className="mx-auto max-w-3xl px-6 py-32 text-center">
            <p className="font-display text-2xl">Product not found.</p>
            <Link to="/shop" className="gold-button mt-6 inline-block">Browse the shop</Link>
        </div>
    );
    if (!p) return <div className="flex justify-center py-40"><Loader2 className="animate-spin text-gold-300" size={28} /></div>;

    const shipping = p.shipping_charge || 0;
    const total = p.price * qty + shipping;

    return (
        <div data-testid="shop-product-page" className="relative">
            <Seo
                title={p.seo_title || `${p.title} — Buy Now | Abhijit JK Shop`}
                description={p.meta_description || p.short_description || `Buy ${p.title} from Abhijit JK.`}
                path={`/shop/${p.slug}`}
                jsonLd={[breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Shop", path: "/shop" }, { name: p.title, path: `/shop/${p.slug}` }])]}
            />
            <section className="mx-auto max-w-7xl px-6 pt-12 pb-24 lg:px-10">
                <Link to="/shop" data-testid="back-to-shop" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-white"><ArrowLeft size={15} /> Back to shop</Link>
                <div className="mt-8 grid gap-10 lg:grid-cols-[1fr_460px]">
                    {/* LEFT */}
                    <div>
                        <Reveal>
                            <div className="overflow-hidden rounded-lg bg-white/[0.03]">
                                {p.image ? (
                                    <img src={`${BACKEND}${p.image}`} alt={p.title} className="w-full object-cover" />
                                ) : (
                                    <div className="flex aspect-square w-full items-center justify-center"><Package size={64} className="text-gold-300/40" /></div>
                                )}
                            </div>
                        </Reveal>
                        {p.description && (
                            <Reveal delay={0.1}>
                                <div className="mt-8 space-y-4">
                                    {p.description.split(/\n\s*\n/).map((t, i) => <p key={i} className="text-base leading-relaxed text-white/70">{t}</p>)}
                                </div>
                            </Reveal>
                        )}
                        {(p.features || []).length > 0 && (
                            <Reveal delay={0.15}>
                                <div className="mt-10">
                                    <h2 className="font-display text-lg md:text-lg">Highlights</h2>
                                    <ul className="mt-4 space-y-3">
                                        {p.features.map((f, i) => (
                                            <li key={i} className="flex items-start gap-3 text-sm text-white/75"><Check size={15} className="mt-0.5 flex-none text-gold-300" /> {f}</li>
                                        ))}
                                    </ul>
                                </div>
                            </Reveal>
                        )}
                    </div>

                    {/* RIGHT — Buy Now */}
                    <div className="lg:sticky lg:top-28 lg:self-start">
                        <Reveal delay={0.1}>
                            <div className="glass glow-border rounded-lg p-7" data-testid="buy-now-card">
                                {done ? (
                                    <div className="text-center" data-testid="order-success">
                                        <PartyPopper className="mx-auto text-gold-300" size={34} />
                                        <h3 className="mt-4 font-display text-xl">Order confirmed!</h3>
                                        <p className="mt-2 text-sm text-white/60">Order ID: <span className="gold-text font-medium">{done}</span></p>
                                        <p className="mt-3 text-sm leading-relaxed text-white/60">
                                            Thank you, {form.full_name}. We'll pack and ship <b className="text-white/85">{p.title}</b> to your address and keep you updated on {form.mobile}.
                                        </p>
                                        <Link to="/shop" className="ghost-button mt-6 inline-block">Continue shopping</Link>
                                    </div>
                                ) : (
                                    <>
                                        {p.category && <p className="text-[11px] uppercase tracking-wider text-white/45">{p.category}</p>}
                                        <h1 className="mt-2 font-display text-2xl font-semibold leading-tight sm:text-3xl">{p.title}</h1>
                                        {p.short_description && <p className="mt-3 text-sm leading-relaxed text-white/60">{p.short_description}</p>}
                                        <p className="mt-5 font-display text-3xl gold-text">₹{(p.price || 0).toLocaleString("en-IN")}
                                            {p.strike_price && <span className="ml-3 text-lg text-white/35 line-through">₹{p.strike_price.toLocaleString("en-IN")}</span>}
                                        </p>
                                        <p className="mt-1.5 flex items-center gap-2 text-xs text-white/45">
                                            <Truck size={13} className="text-gold-300" /> {shipping > 0 ? `Shipping ₹${shipping.toLocaleString("en-IN")}` : "Free shipping"} · Ships across India
                                        </p>

                                        {p.sold_out ? (
                                            <div className="mt-6 rounded-md bg-red-500/10 p-4 text-center text-sm text-red-300" data-testid="sold-out-notice">
                                                Currently sold out. Please check back soon.
                                            </div>
                                        ) : (
                                            <>
                                                <div className="mt-6 flex items-center gap-4">
                                                    <span className="text-xs uppercase tracking-wider text-white/50">Quantity</span>
                                                    <div className="flex items-center gap-3 rounded-md border border-white/10 bg-white/5 px-2 py-1.5">
                                                        <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-1 text-white/60 hover:text-white"><Minus size={14} /></button>
                                                        <span data-testid="qty-value" className="w-6 text-center font-display">{qty}</span>
                                                        <button data-testid="qty-plus" onClick={() => setQty((q) => Math.min(20, q + 1))} className="p-1 text-white/60 hover:text-white"><Plus size={14} /></button>
                                                    </div>
                                                </div>

                                                <p className="mt-6 text-xs uppercase tracking-wider text-gold-300/80">Shipping details</p>
                                                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                                                    <F label="Full name *"><input data-testid="ship-name" className={inputCls} value={form.full_name} onChange={set("full_name")} placeholder="Your name" /></F>
                                                    <F label="Mobile *"><input data-testid="ship-mobile" className={inputCls} value={form.mobile} onChange={set("mobile")} placeholder="10-digit mobile" /></F>
                                                    <F label="Email *" full><input data-testid="ship-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" /></F>
                                                    <F label="Full address *" full><input data-testid="ship-address" className={inputCls} value={form.address} onChange={set("address")} placeholder="House / street / landmark" /></F>
                                                    <F label="Pincode *"><input data-testid="ship-pincode" className={inputCls} value={form.pincode} onChange={set("pincode")} placeholder="600001" /></F>
                                                    <F label="City *"><input data-testid="ship-city" className={inputCls} value={form.city} onChange={set("city")} placeholder="City" /></F>
                                                    <F label="State *" full><input data-testid="ship-state" className={inputCls} value={form.state} onChange={set("state")} placeholder="State" /></F>
                                                </div>

                                                <div className="mt-6 space-y-1.5 rounded-md bg-white/[0.03] p-4 text-sm">
                                                    <div className="flex justify-between text-white/60"><span>{p.title} × {qty}</span><span>₹{(p.price * qty).toLocaleString("en-IN")}</span></div>
                                                    <div className="flex justify-between text-white/60"><span>Shipping</span><span>{shipping > 0 ? `₹${shipping.toLocaleString("en-IN")}` : "Free"}</span></div>
                                                    <div className="flex justify-between border-t border-white/10 pt-2 font-display text-base"><span>Total</span><span className="gold-text" data-testid="order-total">₹{total.toLocaleString("en-IN")}</span></div>
                                                </div>

                                                <button data-testid="buy-now-btn" onClick={buyNow} disabled={busy}
                                                    className="gold-button mt-6 inline-flex w-full items-center justify-center gap-2 disabled:opacity-60">
                                                    {busy ? <Loader2 size={15} className="animate-spin" /> : <CreditCard size={15} />} Buy Now — Pay ₹{total.toLocaleString("en-IN")}
                                                </button>
                                                <p className="mt-4 flex items-center justify-center gap-2 text-[11px] text-white/40">
                                                    <ShieldCheck size={12} className="text-gold-300" /> Secure Razorpay payment · Direct checkout, no cart
                                                </p>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>
        </div>
    );
}
